import { createActionGroup, emptyProps, props } from '@ngrx/store';
import {
    IAdjustmentAccountListResponse,
    IAdjustmentDetailResponse,
    IAdjustmentListResponse,
    IAdjustmentTransferResponse,
    ICreateAdjustmentRequest,
    IEditAdjustmentRequest,
} from '../models/closing-adjustment.model';
import { AdjustmentFilterKey, AdjustmentSort, AdjustmentTransferMethod } from '../types/closing-adjustments.types';

/**
 * REV-21375 — the correction-booking slice (ADR-0015).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * ⚠️ **WHAT IS DELIBERATELY ABSENT, and asserted in the backend's own negative contract** (§8):
 *
 *   - no `Accept Proposal` and no `Reject Proposal`. The agent proposes no correction bookings in this
 *     flow, so nothing can be in a state where either would apply. An action for it could not be
 *     exercised, and an action nobody can exercise is an action somebody eventually dispatches;
 *   - no `Mark As Booked`. `Gebucht` comes out of the imported ERP journal (§2). The only action that
 *     leads there is `Resolve Ambiguity`, and it names WHICH of the candidates the reconciliation
 *     already found is the proof — it cannot invent one;
 *   - no `Delete Adjustment`. Discarding is a state (§7), and it is named `Discard` so no layer can
 *     confuse the two.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 */
export const closingAdjustmentActions = createActionGroup({
    source: 'ABCP Closing Adjustments',
    events: {
        /**
         * The closing-wide read.
         *
         * `fiscalYear` and `filter` ride along rather than being read from state inside the effect: the
         * effect must load exactly what was asked for, or a fast second click resolves against the
         * wrong one — the same reasoning the document slice records for its own load.
         *
         * `fiscalYear: null` means "the current one", and the SERVER decides which that is: it reads the
         * closing's balance-sheet date, and a client computing it from the clock would be wrong for
         * every mandate whose fiscal year is not the calendar year.
         */
        'Load Adjustments': props<{
            projectId: string;
            fiscalYear: number | null;
            filter: AdjustmentFilterKey;
        }>(),
        'Load Adjustments Success': props<{ response: IAdjustmentListResponse }>(),
        'Load Adjustments Failure': emptyProps(),

        /**
         * Re-ask for the CURRENT year and pill, which the slice already holds.
         *
         * What a caller dispatches when it knows WHICH closing but not WHAT is on screen — which is
         * every caller that is not itself the gesture being answered: the tab page on mount (it remounts
         * on every return from the detail route, and a hardcoded `'all'` there would reset the pill), and
         * every write effect's follow-up.
         *
         * `projectId` IS carried, because on the very first load the slice does not know it yet.
         */
        'Reload Adjustments': props<{ projectId: string }>(),

        /** Display state the host owns. A filter change RELOADS: which rows exist is the server's call. */
        'Set Filter': props<{ filter: AdjustmentFilterKey }>(),
        'Set Fiscal Year': props<{ fiscalYear: number }>(),
        /** Sorting is display state only — the list is short (one closing-year), so the client orders it. */
        'Set Sort': props<{ sort: AdjustmentSort | null }>(),
        'Set Search Term': props<{ searchTerm: string }>(),

        /** The detail surface, including the candidate lines when the match is ambiguous. */
        'Load Adjustment': props<{ projectId: string; adjustmentId: string }>(),
        'Load Adjustment Success': props<{ response: IAdjustmentDetailResponse }>(),
        'Load Adjustment Failure': emptyProps(),
        /** Leaving the detail route — so a stale correction cannot flash when another is opened. */
        'Clear Adjustment': emptyProps(),

        /** Entering a correction by hand — the ONLY way one comes into existence (§6). */
        'Create Adjustment': props<{ projectId: string; request: ICreateAdjustmentRequest }>(),
        'Create Adjustment Success': props<{ response: IAdjustmentDetailResponse }>(),
        /**
         * `errorKey` and never a message: the backend refuses with a stable key, and prose from a server
         * is prose nobody translated (AC13).
         */
        'Create Adjustment Failure': props<{ errorKey: string }>(),

        /** Editing an OPEN correction. The backend refuses anything else (§7). */
        'Edit Adjustment': props<{ projectId: string; adjustmentId: string; request: IEditAdjustmentRequest }>(),
        'Edit Adjustment Success': props<{ response: IAdjustmentDetailResponse }>(),
        'Edit Adjustment Failure': props<{ errorKey: string }>(),

        /** Discarding an OPEN correction — a STATE, never a delete (§7). */
        'Discard Adjustment': props<{ projectId: string; adjustmentId: string }>(),
        'Discard Adjustment Success': emptyProps(),
        'Discard Adjustment Failure': props<{ errorKey: string }>(),

        // `Verworfen` → `Freigegeben`. The counterpart of discarding, which is why discarding was
        // never a delete: a withdrawal nobody can undo is a delete with extra steps.
        'Restore Adjustment': props<{ projectId: string; adjustmentId: string }>(),
        'Restore Adjustment Success': emptyProps(),
        'Restore Adjustment Failure': props<{ errorKey: string }>(),

        /**
         * Transferring a SELECTION with one of three targets (§3).
         *
         * ⚠️ `adjustmentIds` is carried explicitly rather than derived in the effect from "everything
         * open": the user's selection and the current filter are different things, and a transfer that
         * silently meant "all open" would post bookings the user did not choose.
         */
        'Transfer Adjustments': props<{
            projectId: string;
            adjustmentIds: string[];
            method: AdjustmentTransferMethod;
        }>(),
        'Transfer Adjustments Success': props<{ response: IAdjustmentTransferResponse }>(),
        'Transfer Adjustments Failure': props<{ errorKey: string }>(),
        /** The user read the transfer summary — clears it so it does not reappear on the next visit. */
        'Clear Transfer Result': emptyProps(),

        // ── The IN-ACCOUNT list (REV-21364) ──────────────────────────────────────────────────────
        //
        // Its own actions and its own branch of the state, NOT the closing-wide ones. The two lists
        // answer different questions and are reached by different routes; sharing `adjustments` would
        // mean one surface's year change or reload silently rewrote the other's rows.
        'Load Account Adjustments': props<{
            projectId: string;
            accountId: string;
            fiscalYear: number | null;
            /** The reload after a write: keeps the rows on screen instead of showing placeholders. */
            background?: boolean;
        }>(),
        'Load Account Adjustments Success': props<{ response: IAdjustmentAccountListResponse }>(),
        'Load Account Adjustments Failure': emptyProps(),
        'Set Account Fiscal Year': props<{ fiscalYear: number }>(),
        'Clear Account Adjustments': emptyProps(),

        /**
         * Resolving an AMBIGUOUS journal match (§4, AC7).
         *
         * ⚠️ The ONE action that leads to `Gebucht`, and it does not break §2 — it confirms it: the
         * proof still comes from the journal, and `journalEntryId` must be one the reconciliation served.
         * The backend refuses anything else.
         */
        'Resolve Ambiguity': props<{ projectId: string; adjustmentId: string; journalEntryId: string }>(),
        'Resolve Ambiguity Success': props<{ response: IAdjustmentDetailResponse }>(),
        'Resolve Ambiguity Failure': props<{ errorKey: string }>(),
    },
});
